import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, Eye, Settings, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../lib/api';
import { useOrganizations } from '../hooks/useOrganizations';
import Chip from '../components/Chip';
import Button from '../components/Button';
import ActionsHeaderHelp from '../components/ActionsHeaderHelp';
import MobileOrganizationWorkspaceList from '../components/cards/MobileOrganizationWorkspaceList';
import { useTheme } from '@shared/theme/ThemeContext';
import { usePageMetadata } from '../contexts/PageMetadataContext';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../components/StandardTable';
import LoadingList from '@shared/ui/LoadingList';
import FormModal from '@shared/ui/FormModal';
import ViewToggle from '../components/ViewToggle';

interface Workspace {
  id: string;
  name: string;
  description?: string;
  owner_org_id: string;
  owner_org_name: string;
  owner_org_subscription?: string;
  has_subscription?: boolean;
  created_at: string;
  updated_at: string;
  bill_count?: number;
  can_manage?: boolean;
  logo_file?: string;
  logo_url?: string;
}

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkspaceCreated: () => void;
  organizations: Array<{
    id: string;
    name: string;
    role: string;
  }>;
}

const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  isOpen,
  onClose,
  onWorkspaceCreated,
  organizations
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ownerOrgId, setOwnerOrgId] = useState('');
  const [loading, setLoading] = useState(false);

  // Get eligible organizations (only org_admin or system_admin roles can create workspaces)
  const eligibleOrganizations = organizations.filter(
    org => ['system_admin', 'org_admin'].includes(org.role?.toLowerCase())
  );

  // Auto-select organization if only one is available
  useEffect(() => {
    if (isOpen && eligibleOrganizations.length === 1 && !ownerOrgId) {
      setOwnerOrgId(eligibleOrganizations[0].id);
    }
  }, [isOpen, eligibleOrganizations, ownerOrgId]);

  const handleSubmit = async () => {
    if (!name.trim() || !ownerOrgId) return;

    setLoading(true);
    try {
      await api.post('/workspaces', {
        name: name.trim(),
        description: description.trim(),
        ownerOrgId
      });

      toast.success('Workspace created successfully');
      onWorkspaceCreated();
      onClose();
      
      // Reset form
      setName('');
      setDescription('');
      setOwnerOrgId('');
    } catch (error: any) {
      console.error('Error creating workspace:', error);
      toast.error(error.response?.data?.message || 'Failed to create workspace');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      title="Create workspace"
      onClose={onClose}
      onSubmit={handleSubmit}
      size="sm"
      submitLabel="Create workspace"
      isSubmitting={loading}
      submitDisabled={!name.trim() || !ownerOrgId}
      isDirty={name.trim().length > 0}
    >
      <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Workspace Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="Enter workspace name"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="input"
              placeholder="Enter workspace description"
            />
          </div>

          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Owner Organization *
            </label>
            <select
              id="organization"
              value={ownerOrgId}
              onChange={(e) => setOwnerOrgId(e.target.value)}
              className="input"
              required
            >
              {eligibleOrganizations.length !== 1 && (
                <option value="">Select organization</option>
              )}
              {eligibleOrganizations.map(org => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

      </div>
    </FormModal>
  );
};

const WorkspacesPage: React.FC = () => {
  const { theme } = useTheme();
  const { setPageMetadata } = usePageMetadata();
  
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Load view mode preference from localStorage (desktop only)
  const [viewMode, setViewMode] = useState<'card' | 'table'>(() => {
    const saved = localStorage.getItem('workspacesViewMode');
    return (saved === 'card' || saved === 'table') ? saved : 'table';
  });
  const [initialViewSet, setInitialViewSet] = useState(false);

  // Save view mode preference when it changes (but not on initial auto-set)
  useEffect(() => {
    if (initialViewSet) {
      localStorage.setItem('workspacesViewMode', viewMode);
    }
  }, [viewMode, initialViewSet]);
  const [sortBy, setSortBy] = useState<'name' | 'billCount'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();
  
  const { organizations } = useOrganizations();

  // Sort workspaces based on current sort settings
  const sortedWorkspaces = [...workspaces].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    if (sortBy === 'name') {
      aValue = a.name.toLowerCase();
      bValue = b.name.toLowerCase();
    } else {
      aValue = a.bill_count ?? 0;
      bValue = b.bill_count ?? 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Handle sorting
  const handleSort = (field: string) => {
    const sortField = field as 'name' | 'billCount';
    if (sortBy === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sortField);
      setSortDirection('asc');
    }
  };

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      const response = await api.get('/workspaces');
      setWorkspaces(response.data);
      
      // Set default view based on number of workspaces (only on initial load)
      if (!initialViewSet) {
        const workspaceCount = response.data.length;
        setViewMode(workspaceCount <= 9 ? 'card' : 'table');
        setInitialViewSet(true);
      }
    } catch (error: any) {
      console.error('Error fetching workspaces:', error);
      toast.error('Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  };

  // Handle workspace click with subscription check
  const handleWorkspaceClick = (workspace: Workspace) => {
    if (workspace.has_subscription === false) {
      toast.error('This workspace requires an active subscription. Please upgrade to access.', {
        duration: 4000,
        icon: '🔒',
      });
      return;
    }
    navigate(`/workspaces/${workspace.id}`);
  };

  useEffect(() => {
    fetchWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update page title with workspace count
  useEffect(() => {
    if (!loading) {
      setPageMetadata({
        title: `Workspaces (${workspaces.length})`,
        description: 'Manage and organize your legislative tracking workspaces'
      });
    }
    return () => {
      setPageMetadata(null);
    };
  }, [loading, workspaces.length, setPageMetadata]);

  const handleWorkspaceCreated = () => {
    // Don't reset the view mode when a workspace is created
    // The user has already made a choice, so preserve it
    fetchWorkspaces();
  };



  // Check if user can create workspaces (has org admin role in at least one org)
  const canCreateWorkspace = organizations.some(org => 
    ['system_admin', 'org_admin'].includes(org.role?.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6 pt-2">
        <LoadingList rows={4} lines={3} label="Loading workspaces" />
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6 pt-0 md:pt-2">
      {/* New Workspace Button - Hide on mobile, show on desktop */}
      <div className="hidden md:flex items-center justify-end">
        {canCreateWorkspace && (
          <Button
            variant="primary"
            size="md"
            icon={Plus}
            onClick={() => setShowCreateModal(true)}
          >
            New Workspace
          </Button>
        )}
      </div>

      {/* View Toggle and Count - Desktop only */}
      <div className="hidden lg:flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <div className="flex items-center gap-4">
          <p className="text-sm text-neutral-600">
            {workspaces.length.toLocaleString()} workspaces found
          </p>
        </div>
      </div>

      {workspaces.length === 0 ? (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <Users className="w-12 h-12 mx-auto mb-4 text-text-tertiary dark:text-text-dark-tertiary" />
            <h3 className="text-lg font-medium mb-2 text-text-primary dark:text-text-dark-primary">
              No workspaces yet
            </h3>
            <p className="mb-6 text-text-secondary dark:text-text-dark-secondary">
              Workspaces help you organize and collaborate on your work with team members.
            </p>
            {canCreateWorkspace && (
              <Button
                variant="primary"
                size="md"
                icon={Plus}
                onClick={() => setShowCreateModal(true)}
                className="mx-auto"
              >
                Create your first workspace
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Mobile: Hierarchical Organization/Workspace View (< 768px) */}
          <div className="md:hidden">
            <MobileOrganizationWorkspaceList
              workspaces={sortedWorkspaces}
              organizations={organizations}
              onWorkspaceClick={handleWorkspaceClick}
            />
            
            {/* New Workspace Button - Mobile only, at bottom */}
            {canCreateWorkspace && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="primary"
                  size="lg"
                  icon={Plus}
                  onClick={() => setShowCreateModal(true)}
                  fullWidth
                  className="max-w-md"
                >
                  New Workspace
                </Button>
              </div>
            )}
          </div>

          {/* Desktop: Table or Card View (>= 768px) */}
          <div className="hidden md:block">
            {viewMode === 'card' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedWorkspaces.map((workspace) => (
            <div
              key={workspace.id}
              className="rounded-lg p-6 transition-shadow"
              style={{
                backgroundColor: theme === 'dark' ? 'rgba(241, 244, 249, 0.05)' : 'rgba(255, 255, 255, 0.9)',
                borderColor: theme === 'dark' ? 'rgba(241, 244, 249, 0.2)' : 'rgba(62, 39, 80, 0.2)',
                border: `1px solid ${theme === 'dark' ? 'rgba(241, 244, 249, 0.2)' : 'rgba(62, 39, 80, 0.2)'}`,
                outline: 'none',
                boxShadow: theme === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = theme === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = theme === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {workspace.logo_url ? (
                    <img 
                      src={workspace.logo_url} 
                      alt={`${workspace.name} logo`}
                      className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-border-light dark:border-border-dark"
                    />
                  ) : (
                    <div 
                      className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center border border-border-light dark:border-border-dark"
                      style={{ backgroundColor: theme === 'dark' ? 'rgba(241, 244, 249, 0.1)' : 'rgba(62, 39, 80, 0.08)' }}
                    >
                      <Users className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <h3 
                      className="font-semibold text-lg truncate"
                      style={{ color: theme === 'dark' ? '#F1F4F9' : '#3E2750' }}
                    >
                      {workspace.name}
                    </h3>
                    {workspace.has_subscription === false && (
                      <Lock className="w-4 h-4 text-yellow-500 flex-shrink-0" aria-label="Subscription required" />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <button
                    onClick={() => handleWorkspaceClick(workspace)}
                    className="p-1 text-neutral-500 hover:text-primary-600 transition-colors"
                    title={workspace.has_subscription === false ? "Subscription required" : "View workspace"}
                  >
                    {workspace.has_subscription === false ? (
                      <Lock className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                  {/* Show settings only for users with manage workspace access */}
                  {workspace.can_manage && (
                    <button
                      onClick={() => navigate(`/workspaces/${workspace.id}?tab=manage`)}
                      className="p-1 text-neutral-500 hover:text-primary-600 transition-colors"
                      title="Manage workspace"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {workspace.description && (
                <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
                  {workspace.description}
                </p>
              )}

              <div className="space-y-2 text-sm text-neutral-500">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span>{workspace.owner_org_name}</span>
                  {/* Show admin badge if user has org_admin role for this workspace */}
                  {organizations.some(org => 
                    ['system_admin', 'org_admin'].includes(org.role?.toLowerCase()) && 
                    org.id === workspace.owner_org_id
                  ) && (
                    <Chip 
                      label="Admin"
                      variant="success"
                      size="sm"
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{workspace.bill_count ?? 0} bills tracked</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border-light">
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => handleWorkspaceClick(workspace)}
                  fullWidth
                  className={workspace.has_subscription === false 
                    ? "bg-yellow-50 text-yellow-700 hover:bg-yellow-100 font-medium" 
                    : "bg-primary-50 text-primary-700 hover:bg-primary-100 font-medium"}
                >
                  {workspace.has_subscription === false ? (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Subscription Required
                    </>
                  ) : (
                    'Open Workspace'
                  )}
                </Button>
              </div>
            </div>
          ))}
              </div>
            ) : (
              <StandardTable>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell
                sortable
                sortKey="name"
                currentSortKey={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                columnType="auto"
              >
                Workspace
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable
                sortKey="billCount"
                currentSortKey={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                columnType="fit"
                responsiveHide="sm"
              >
                Bills Tracked
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">
                <span className="inline-flex items-center gap-1.5">
                  Actions
                  <ActionsHeaderHelp
                    icons={['view', { key: 'manage', note: 'Workspaces you can manage.' }]}
                  />
                </span>
              </StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {sortedWorkspaces.map((workspace) => (
              <StandardTableRow
                key={workspace.id}
                onClick={() => handleWorkspaceClick(workspace)}
                className="cursor-pointer"
              >
                <StandardTableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-text-primary dark:text-text-dark-primary">
                      {workspace.name}
                    </span>
                    {workspace.has_subscription === false && (
                      <Chip 
                        label="Locked"
                        variant="warning"
                        size="sm"
                        icon={Lock}
                      />
                    )}
                    {organizations.some(org => 
                      ['system_admin', 'org_admin'].includes(org.role?.toLowerCase()) && 
                      org.id === workspace.owner_org_id
                    ) && (
                      <Chip 
                        label="Admin"
                        variant="success"
                        size="sm"
                      />
                    )}
                  </div>
                </StandardTableCell>
                <StandardTableCell responsiveHide="sm">
                  <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    {workspace.bill_count ?? 0} bills
                  </span>
                </StandardTableCell>
                <StandardTableCell>
                  <div className="flex items-center gap-2">
                    <button 
                      className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/workspaces/${workspace.id}`);
                      }}
                      title="View workspace"
                      aria-label="View workspace"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {workspace.can_manage && (
                      <button 
                        className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/workspaces/${workspace.id}?tab=manage`);
                        }}
                        title="Manage workspace"
                        aria-label="Manage workspace"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </StandardTableCell>
              </StandardTableRow>
            ))}
          </StandardTableBody>
              </StandardTable>
            )}
          </div>
        </>
      )}

      <CreateWorkspaceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onWorkspaceCreated={handleWorkspaceCreated}
        organizations={organizations}
      />
    </div>
  );
};

export default WorkspacesPage; 